/**
 * DominoGame (#390 PR B) — full-screen overlay for the elderly-adapted
 * domino game. First interactive on-screen game in AmigoMio.
 *
 * Design (locked across 10 questions with Bas 2026-05-06):
 *  - Block variant double-six, 7 tiles each, no scoring, no timer
 *  - Touch-only moves (tap tile → tap chain end). Mic stays open
 *    during user's turn for free conversation, but moves come
 *    deterministically through touch — voice intent parsing for
 *    moves would misfire too often.
 *  - Berta's hand face-down at the top, visibly shrinking stack.
 *  - User's hand at the bottom, large photorealistic ivory tiles.
 *  - Chain in the middle. Horizontal scroll if it overflows.
 *  - Photorealistic tile style — drilled black pips on cream-ivory,
 *    soft drop shadow. Warmest of the three options we considered.
 *  - Responsive: tile size clamps with viewport so the same
 *    component works on small phones, big tablets, and everything
 *    in between. Touch targets stay ≥60×60 pt regardless.
 *
 * State protocol (server-driven):
 *  Inbound from server:
 *   - {type: domino_start, state: GameState}
 *   - {type: domino_state, state: GameState}  (after every move)
 *   - {type: domino_end, reason, winner?}
 *   - {type: domino_error, message}
 *  Outbound to server:
 *   - {type: domino_play, tile: [a,b], end: 'left'|'right'}
 *   - {type: domino_knock}
 *   - {type: domino_quit}
 *
 * GameState shape (from server's _serialise_domino_state):
 *   {
 *     chain: [{tile: [a,b], left_value, right_value, is_double}, ...],
 *     user_hand: [[a,b], ...],
 *     berta_hand_count: number,
 *     current_player: 'user' | 'berta',
 *     left_end: number | null,
 *     right_end: number | null,
 *     consecutive_knocks: number,
 *     winner: null | 'user' | 'berta' | 'tie',
 *     last_action: {type: 'play'|'knock', player, tile?, end?, ...} | null,
 *   }
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useActivityBox } from './ActivityFrame';
import { useLang, formatString } from '../i18n/LanguageContext';

// #974 — client-side "thinking" pause before Berta's move renders.
// Pre-#974 the SERVER slept 1.2–2.5s in the domino handler to make
// Berta feel considered — but that `asyncio.sleep` ran inside the
// receive loop and starved audio-frame intake (the #972 hang). The
// pause now lives ENTIRELY on the tablet: the server applies Berta's
// move and pushes `domino_state` immediately, and we hold it here for
// a beat so the board still updates deliberately rather than instantly.
export const BERTA_THINK_MS = 1400;

/**
 * #974 — pure render-planner for an incoming domino GameState.
 *
 * Post-#974 the server runs NO voice channel during a game: Berta's
 * move arrives only as a `domino_state` push, and her pass (knock) has
 * no spoken "Hmm, ik moet overslaan" cue any more. So the tablet is the
 * ONLY thing that conveys her move — which means:
 *   - her move should render after a short client-side delay (so it
 *     feels considered, replacing the deleted server sleep), and
 *   - her pass needs a VISIBLE badge (it produces no board change and
 *     no longer has a voice line, so without a badge it's invisible).
 *
 * Pure so it can be unit-tested without rendering React (this project
 * has no @testing-library/react — see DominoGame tests).
 *
 * @param incoming the just-arrived GameState (server's _serialise_domino_state)
 * @param prev     the previously-received GameState prop (or null on first)
 * @returns {{isBertaAction, isBertaKnock, isBertaPlay, delayRender}}
 */
export function planDominoRender(incoming, prev) {
  const la = incoming && incoming.last_action;
  const isBertaAction = !!(la && la.player === 'berta');
  const isBertaKnock = !!(isBertaAction && la.type === 'knock');
  const isBertaPlay = !!(isBertaAction && la.type === 'play');
  // Only delay a NEWLY-arrived Berta action. A metadata-only re-push
  // — e.g. the `domino_end` winner merge, which keeps `last_action`
  // identical — must NOT re-trigger the thinking delay (that would
  // double-pause and feel broken). Compare the serialised last_action.
  const prevLa = prev && prev.last_action;
  const actionChanged =
    JSON.stringify(prevLa || null) !== JSON.stringify(la || null);
  const delayRender = isBertaAction && actionChanged;
  return { isBertaAction, isBertaKnock, isBertaPlay, delayRender };
}

// #441 Phase 2 — DominoGame i18n bucket.
// #551 — extended to cover the empty-chain hint + GameEndCard
// title/subtitle for all three winner outcomes (user / berta / tie).
// English fallbacks preserved so the component renders without a
// LanguageProvider in the tree (tests / storybook).
function _useStrings() {
  const ctx = useLang();
  const s = ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.dominogame;
  return {
    done: (s && s.done) || 'Done',
    doneAria: (s && s.doneAria) || 'End the domino game',
    knock: (s && s.knock) || 'Knock (skip)',
    knockAria: (s && s.knockAria) || 'Knock — skip turn (only when you have no playable tile)',
    // #974 — single-audio-channel domino: Berta no longer SPEAKS her
    // move/pass, so the board carries these cues visually instead.
    thinking: (s && s.thinking) || '{name} is thinking…',
    passed: (s && s.passed) || '{name} passed',
    bertaStackLabel: (s && s.bertaStackLabel) || '{name} — {count} tiles',
    userTilesLabel: (s && s.userTilesLabel) || 'YOUR TILES — {count} tiles',
    userTilesYourTurn: (s && s.userTilesYourTurn) || ' • your turn',
    placeOnLeft: (s && s.placeOnLeft) || 'Place tile on left end of chain',
    placeOnRight: (s && s.placeOnRight) || 'Place tile on right end of chain',
    // #551
    emptyChainHint:    (s && s.emptyChainHint)    || 'Tap a tile from your hand, then tap here to start',
    endTitleUser:      (s && s.endTitleUser)      || 'What a lovely game!',
    endSubtitleUser:   (s && s.endSubtitleUser)   || 'You played beautifully.',
    endTitleBerta:     (s && s.endTitleBerta)     || 'What a lovely game!',
    endSubtitleBerta:  (s && s.endSubtitleBerta)  || 'Wonderful playing with you.',
    endTitleTie:       (s && s.endTitleTie)       || 'A perfectly balanced game!',
    endSubtitleTie:    (s && s.endSubtitleTie)    || 'Both of us played well.',
  };
}


// ── Viewport detection ────────────────────────────────────────────────────
//
// PR C polish (#390): the DominoGame is no longer a pure full-screen
// overlay — it shares the screen with a CallStrip showing Berta's
// status + last reply (so the user has context during play, per Bas's
// design feedback). Layout depends on viewport:
//
//   tablet-landscape (≥900 wide, landscape) → CallStrip on the right (30%),
//                                              game on the left (70%)
//   tablet-portrait (≥600 tall, portrait, ≥600 wide)
//                                            → CallStrip on top (35%),
//                                              game on bottom (65%)
//   phone-landscape (landscape, <900 wide)   → top strip 35%, game 65%
//                                              (treated like tablet-portrait;
//                                              the actual game still gets
//                                              majority of the space)
//   phone-portrait (<600 wide, portrait)     → 80px top pill, game below
//
// Touch targets ≥60×60 throughout regardless of viewport.

// useViewportMode promoted to web/src/hooks/useViewportMode.js (#405)
// so the default call view in TabletApp and any future media-area
// splits can share the same break-point definitions.

const BRAND_ORANGE = '#E8845A';

// Photorealistic ivory palette
const IVORY_BG = '#F8F0DC';        // tile face
const IVORY_BG_DIMMED = '#E0D6BC';  // unplayable tile (slightly desaturated)
const IVORY_BORDER = '#D5C896';     // tile edge
const PIP_COLOUR = '#2A2418';       // drilled-black pip
const TABLE_GREEN = '#0A4D32';      // felt table
const HIGHLIGHT_GREEN = '#7ED957';  // playable indicator + chain-end target
const HIGHLIGHT_GLOW = 'rgba(126, 217, 87, 0.55)';

// Pip layout within a half-tile (0..1 in both x and y, half-tile coords)
const PIP_POSITIONS = {
  0: [],
  1: [[0.5, 0.5]],
  2: [[0.25, 0.25], [0.75, 0.75]],
  3: [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
  4: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
  5: [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
  6: [[0.25, 0.2], [0.75, 0.2], [0.25, 0.5], [0.75, 0.5], [0.25, 0.8], [0.75, 0.8]],
};


// ── Atomic components ──────────────────────────────────────────────────────


/**
 * Compute the domino tile WIDTH in CSS px from the ActivityFrame's measured
 * content box. Tiles render 2:1, so a tile's HEIGHT is 2× the returned size
 * (see `Tile`). #1096.
 *
 * Two layout models, because the board is arranged differently by shape:
 *
 *  - Portrait / tall — a single vertical stack: Berta (~80) + chain
 *    (minHeight 2.4·T) + hand (2·T + ~49 chrome) + knock (~72) ≈ 4.4·T +
 *    ~217. Width-driven, floored at 60 so portrait stays generous.
 *
 *  - Short landscape (wide AND < 560 tall, e.g. Samsung A9 893×399) — a
 *    2-column board: the wide LEFT column stacks only chain + hand; Berta
 *    and the knock button move to a side rail and no longer cost vertical
 *    space. Here WIDTH binds (≈7 hand tiles across the ~66% left column),
 *    which keeps tiles ≈legible (~59) instead of the ~41 they'd shrink to
 *    just to fit the old vertical stack — that shrink was the "half stones
 *    at the bottom" clip.
 *
 * Clamped to [48, 110]: 110 caps 12" tablets; 48 is the hard touch floor
 * for a box too short even for the 2-column board (the game area still
 * scrolls as the ultimate fallback). Fallback 700 covers first paint.
 */
export function computeDominoTileSize(boxWidth, boxHeight) {
  const w = boxWidth || 700;
  const h = boxHeight || 700;
  const shortLandscape = boxWidth > 0 && w > h && h < 560;
  if (shortLandscape) {
    const byHeight = (h - 65) / 4.4;   // left col: chain 2.4·T + hand 2·T + chrome
    const byWidth = (w * 0.66) / 8;    // ~7 hand tiles + gaps across the left col
    return Math.round(Math.max(48, Math.min(110, Math.min(byWidth, byHeight))));
  }
  const byWidth = Math.max(60, w * 0.075);
  const byHeight = (h - 120) / 5.6;
  return Math.round(Math.max(60, Math.min(110, Math.min(byWidth, byHeight))));
}


/**
 * Render a single domino tile as SVG. `size` is the *width* in CSS px;
 * height auto-derives at 2:1 ratio. `dimmed` desaturates for the
 * unplayable indicator. Tile is fully responsive — feed it a CSS
 * variable / clamp() for `size` and it scales.
 */
function Tile({ tile, size = 100, dimmed = false, selected = false, onClick = null,
                ariaLabel = null, faceDown = false }) {
  const [a, b] = tile || [0, 0];
  const w = size;
  const h = size * 2;

  const fill = dimmed ? IVORY_BG_DIMMED : IVORY_BG;
  const cursor = onClick ? 'pointer' : 'default';
  // Touch-target safety net: even when size shrinks, ensure ≥60×60 hit
  // zone via padding around the SVG itself (handled in the parent).

  if (faceDown) {
    // Berta's hidden tiles — solid back with a subtle decorative pattern
    return (
      <svg
        width={w} height={h}
        viewBox="0 0 100 200"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
        aria-label="Berta's tile (face-down)"
      >
        <rect x="2" y="2" width="96" height="196" rx="8" ry="8"
              fill="#8B5A3C" stroke="#5C3A22" strokeWidth="2"/>
        <rect x="10" y="10" width="80" height="180" rx="4" ry="4"
              fill="none" stroke="#5C3A22" strokeWidth="1.5" opacity="0.45"/>
        {/* Decorative diagonal lines for visual interest */}
        <line x1="10" y1="100" x2="90" y2="100" stroke="#5C3A22" strokeWidth="1" opacity="0.3"/>
      </svg>
    );
  }

  return (
    <svg
      width={w} height={h}
      viewBox="0 0 100 200"
      xmlns="http://www.w3.org/2000/svg"
      onClick={onClick}
      style={{ display: 'block', cursor, userSelect: 'none' }}
      role={onClick ? 'button' : 'img'}
      aria-label={ariaLabel || `domino ${a}-${b}`}
      tabIndex={onClick ? 0 : -1}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(e);
        }
      }}
    >
      {/* Tile body */}
      <rect
        x="2" y="2" width="96" height="196"
        rx="8" ry="8"
        fill={fill}
        stroke={selected ? BRAND_ORANGE : IVORY_BORDER}
        strokeWidth={selected ? 5 : 2}
      />
      {/* Center divider */}
      <line x1="10" y1="100" x2="90" y2="100" stroke={IVORY_BORDER} strokeWidth="2"/>
      {/* Top half pips (value a) */}
      <g>
        {(PIP_POSITIONS[a] || []).map(([px, py], i) => (
          <circle key={`a${i}`} cx={10 + px * 80} cy={10 + py * 80}
                  r="7" fill={PIP_COLOUR}/>
        ))}
      </g>
      {/* Bottom half pips (value b) */}
      <g>
        {(PIP_POSITIONS[b] || []).map(([px, py], i) => (
          <circle key={`b${i}`} cx={10 + px * 80} cy={110 + py * 80}
                  r="7" fill={PIP_COLOUR}/>
        ))}
      </g>
      {/* Glow for playable tiles */}
      {!dimmed && onClick && !selected && (
        <rect x="2" y="2" width="96" height="196" rx="8" ry="8"
              fill="none" stroke={HIGHLIGHT_GREEN} strokeWidth="3" opacity="0.6"/>
      )}
    </svg>
  );
}


/**
 * Berta's stack at the top — face-down tiles fanned/stacked, count
 * label. Visibly shrinks as she plays (Q4 lock).
 */
function BertaStack({ count, isCurrentPlayer, companionName = 'Berta',
                      thinking = false, passed = false }) {
  const tr = _useStrings();
  // Show up to `count` face-down tiles in a row. If count > 7 (rare —
  // edge case), wrap to two rows; in normal play count starts at 7
  // and shrinks.
  const tiles = Array.from({ length: count }, (_, i) => i);
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      padding: '12px 16px',
      background: 'rgba(0,0,0,0.18)',
      borderRadius: 12,
      // Subtle border to highlight Berta's "side" when it's her turn
      border: isCurrentPlayer ? `3px solid ${BRAND_ORANGE}` : '3px solid transparent',
      transition: 'border-color 200ms',
      position: 'relative',
    }}>
      <div style={{
        color: '#F8F0DC', fontSize: '1rem', fontWeight: 600,
        letterSpacing: '0.04em',
      }}>
        {formatString(tr.bertaStackLabel, { name: companionName.toUpperCase(), count })}
      </div>

      {/* #974 — "thinking" badge during the client-side pause before
          Berta's move renders, and a "passed" badge when she knocks
          (her pass has no board change + no voice line any more, so
          this badge is the ONLY signal the elderly user gets). Mutually
          exclusive; the parent never sets both. data-testid kept stable
          for the component test. */}
      {(thinking || passed) && (
        <div
          data-testid={thinking ? 'berta-thinking-badge' : 'berta-passed-badge'}
          role="status"
          aria-live="polite"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginTop: 4,
            padding: '6px 14px',
            borderRadius: 999,
            background: passed ? BRAND_ORANGE : 'rgba(248,240,220,0.18)',
            color: '#F8F0DC',
            fontSize: '0.95rem', fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          {thinking
            ? formatString(tr.thinking, { name: companionName })
            : formatString(tr.passed, { name: companionName })}
        </div>
      )}
      <div style={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
        gap: 'clamp(4px, 0.6vw, 8px)',
      }}>
        {tiles.map((i) => (
          <Tile key={i} tile={[0, 0]} faceDown size={Math.max(40, Math.min(60, 50))} />
        ))}
      </div>
    </div>
  );
}


/**
 * The chain in the middle — all played tiles, left-to-right, with
 * the current ends highlighted as drop targets when the user has
 * selected a tile. Horizontal-scroll if it overflows on narrow
 * screens.
 */
function ChainArea({ chain, leftEnd, rightEnd, selectedTile, onPlace, tileSize }) {
  const tr = _useStrings();
  const showDropTargets = selectedTile !== null;
  const playableLeft = showDropTargets && (selectedTile[0] === leftEnd || selectedTile[1] === leftEnd);
  const playableRight = showDropTargets && (selectedTile[0] === rightEnd || selectedTile[1] === rightEnd);

  // Empty chain edge case — first move can be any tile, both "ends" accept it
  const emptyChain = chain.length === 0;

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      padding: '0 12px',
      background: 'transparent',
      minHeight: tileSize * 2.4,
    }}>
      {/* Left drop target (when applicable) */}
      {(playableLeft || (emptyChain && showDropTargets)) && (
        <DropTarget side="left" tileSize={tileSize}
                    onClick={() => onPlace('left')} />
      )}

      {emptyChain ? (
        <div style={{
          color: 'rgba(248,240,220,0.55)', fontSize: '1.2rem',
          fontStyle: 'italic', padding: '0 24px',
        }}>
          {tr.emptyChainHint}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(2px, 0.4vw, 6px)',
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '8px 4px',
          maxWidth: '100%',
          // Smooth scroll, hide scrollbar on most browsers
          scrollbarWidth: 'thin',
        }}>
          {chain.map((p, idx) => {
            // #390 PR C-2 — doubles render perpendicular to the chain
            // (rotated 90°) so the visual matches a real domino game.
            // Bas's screenshot feedback after PR #393: real doubles are
            // placed sideways across the chain. We keep the chain
            // linear (no L-bends in v1 — that's the bigger v2 polish)
            // but a 90° rotation on doubles is the strong visual cue.
            const isDouble = p.left_value === p.right_value;
            return (
              <div key={idx}
                   style={{
                     display: 'inline-flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     // Reserve a slightly-wider slot for the rotated
                     // double so the chain doesn't visually 'crowd' it
                     width: isDouble ? tileSize * 1.6 : 'auto',
                   }}>
                <div
                  style={{
                    transform: isDouble ? 'rotate(90deg)' : 'none',
                    transformOrigin: 'center',
                  }}>
                  <Tile
                    tile={[p.left_value, p.right_value]}
                    size={tileSize * 0.75}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Right drop target */}
      {playableRight && !emptyChain && (
        <DropTarget side="right" tileSize={tileSize}
                    onClick={() => onPlace('right')} />
      )}
    </div>
  );
}


function DropTarget({ side, tileSize, onClick }) {
  const tr = _useStrings();
  const w = tileSize * 0.75;
  const h = w * 2;
  return (
    <div
      onClick={onClick}
      style={{
        width: w, height: h,
        margin: '0 8px',
        border: `4px dashed ${HIGHLIGHT_GREEN}`,
        borderRadius: 12,
        background: HIGHLIGHT_GLOW,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        animation: 'amg-domino-pulse 1.4s ease-in-out infinite',
      }}
      role="button"
      aria-label={side === 'left' ? tr.placeOnLeft : tr.placeOnRight}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
    >
      <span style={{ color: '#0A4D32', fontWeight: 700, fontSize: '1.4rem' }}>
        {side === 'left' ? '←' : '→'}
      </span>
    </div>
  );
}


/**
 * The user's hand at the bottom. Tiles wrap to multiple rows on
 * narrow screens. Playable tiles glow green; unplayable are dimmed;
 * selected tile gets the orange selection border.
 */
function UserHand({ tiles, leftEnd, rightEnd, selectedTile, onSelect, isCurrentPlayer, tileSize }) {
  const tr = _useStrings();
  const isPlayable = (tile) => {
    // Empty chain — any tile is playable on the first move
    if (leftEnd === null && rightEnd === null) return true;
    return tile[0] === leftEnd || tile[1] === leftEnd
        || tile[0] === rightEnd || tile[1] === rightEnd;
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      padding: '8px 12px 16px',
      background: 'rgba(0,0,0,0.18)',
      borderRadius: 12,
      border: isCurrentPlayer ? `3px solid ${BRAND_ORANGE}` : '3px solid transparent',
      transition: 'border-color 200ms',
    }}>
      <div style={{
        color: '#F8F0DC', fontSize: '0.95rem', fontWeight: 600,
        letterSpacing: '0.04em',
      }}>
        {formatString(tr.userTilesLabel, { count: tiles.length })}
        {isCurrentPlayer && tr.userTilesYourTurn}
      </div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'clamp(6px, 1vw, 14px)',
      }}>
        {tiles.map((tile, idx) => {
          const playable = isPlayable(tile);
          const selected = selectedTile && selectedTile[0] === tile[0] && selectedTile[1] === tile[1];
          // Touch-target safety net: pad the SVG so total clickable
          // area is ≥60×60 even when the tile renders smaller (e.g. on
          // very small phones).
          return (
            <div key={idx}
                 style={{
                   padding: 'clamp(0px, 0.5vw, 6px)',
                   minWidth: 60, minHeight: 60,
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                 }}>
              <Tile
                tile={tile}
                size={tileSize}
                dimmed={!playable}
                selected={selected}
                onClick={isCurrentPlayer && playable
                  ? () => onSelect(selected ? null : tile)
                  : null}
                ariaLabel={`${tile[0]}-${tile[1]} ${playable ? 'playable' : 'cannot play'}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * End-of-game overlay — gentle, no win/lose framing per Q lock.
 *
 * #551 — winner / loser / tie title+subtitle copy now keyed via
 * tablet.dominogame.endTitle{User,Berta,Tie} +
 * endSubtitle{User,Berta,Tie}. The trigger for #551 was Bas's
 * 2026-05-09 staging German repro: the tie-game card rendered
 * "A perfectly balanced game! Both of us played well." in English
 * on a German call.
 */
function GameEndCard({ winner, onClose }) {
  const tr = _useStrings();
  let title, subtitle;
  if (winner === 'user') {
    title = tr.endTitleUser;
    subtitle = tr.endSubtitleUser;
  } else if (winner === 'berta') {
    title = tr.endTitleBerta;
    subtitle = tr.endSubtitleBerta;
  } else {
    title = tr.endTitleTie;
    subtitle = tr.endSubtitleTie;
  }
  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10,
    }}>
      <div style={{
        background: '#F8F0DC',
        padding: '32px 40px',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        textAlign: 'center',
        maxWidth: '90vw',
      }}>
        <h2 style={{
          color: '#2A2418', margin: '0 0 8px',
          fontSize: 'clamp(1.4rem, 4vw, 2rem)',
        }}>{title}</h2>
        <p style={{
          color: '#5C3A22', margin: '0 0 24px',
          fontSize: 'clamp(1rem, 3vw, 1.2rem)',
        }}>{subtitle}</p>
        <button
          onClick={onClose}
          style={{
            background: BRAND_ORANGE, color: 'white',
            border: 'none', padding: '14px 32px',
            fontSize: '1.1rem', fontWeight: 600,
            borderRadius: 8, cursor: 'pointer',
            minHeight: 60, minWidth: 120,
          }}
        >
          {tr.done}
        </button>
      </div>
    </div>
  );
}


// ── Main exported component ────────────────────────────────────────────────


export default function DominoGame({
  state,
  onPlay,
  onKnock,
  onQuit,
  // BertaStack shows the companion's name above her tile count.
  companionName = 'Berta',
}) {
  // Track which tile (if any) the user has tapped to select.
  // Tap-select then tap-end is the elderly-accessible pattern (Q3 lock —
  // no drag-and-drop). Tap the same tile again to deselect.
  const [selectedTile, setSelectedTile] = useState(null);

  const { width: boxWidth, height: boxHeight } = useActivityBox();
  const tr = _useStrings();

  // #974 — client-side delayed render of Berta's move + her knock badge.
  //
  // The board the user SEES is `displayState`, which lags the incoming
  // `state` prop by BERTA_THINK_MS for Berta's moves only. User moves,
  // game start, and metadata-only re-pushes apply immediately. This is
  // what replaces the deleted server-side thinking sleep (#972): the
  // server pushes instantly, the tablet adds the deliberate beat.
  const [displayState, setDisplayState] = useState(state);
  const [bertaThinking, setBertaThinking] = useState(false);
  const [bertaPassed, setBertaPassed] = useState(false);
  const prevStateRef = useRef(state);

  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    const plan = planDominoRender(state, prev);
    if (plan.delayRender) {
      // Hold the incoming Berta state; show "thinking" on her side, then
      // reveal her move (and the "passed" badge if she knocked) after a
      // short beat. The board keeps showing the user's just-played move
      // meanwhile — current_player is already 'berta', so her stack is
      // highlighted, which reads as "her turn, considering".
      setBertaThinking(true);
      setBertaPassed(false);
      const timer = setTimeout(() => {
        setBertaThinking(false);
        setDisplayState(state);
        setBertaPassed(plan.isBertaKnock);
      }, BERTA_THINK_MS);
      return () => clearTimeout(timer);
    }
    // User move / start / metadata update — apply immediately and clear
    // the (now stale) pass badge on the NEXT state, per the #974 AC
    // ("auto-clearing on the next state").
    setBertaThinking(false);
    setDisplayState(state);
    if (!plan.isBertaAction) setBertaPassed(false);
  }, [state]);

  // #1089/#1096 — tiles size from the ActivityFrame's MEASURED content
  // box (not window.innerWidth), fitting by BOTH width and height. The
  // model differs by layout (see computeDominoTileSize): portrait/tall
  // stacks Berta + chain + hand + knock vertically (~4.4 tile-heights of
  // chrome); short landscape uses a 2-column board whose left column only
  // stacks chain + hand, so the tiles stay legible instead of clipping.
  const tileSize = useMemo(
    () => computeDominoTileSize(boxWidth, boxHeight),
    [boxWidth, boxHeight]
  );
  // #1096 — short landscape (e.g. Samsung A9 ~893×399) cannot stack the
  // full board vertically without clipping the bottom ("half stones").
  // Switch to a 2-column board: chain + hand fill the wide left, Berta +
  // knock sit in a compact right rail (~150px of stacked chrome reclaimed).
  const shortLandscape = boxWidth > 0 && boxWidth > boxHeight && boxHeight < 560;

  // Reset selection when the DISPLAYED state changes (e.g. after Berta's
  // move is revealed, or after server rejects a move) so we don't leave
  // a stale selected tile in the user's hand that they might re-tap by
  // mistake. Keyed off displayState (not the raw prop) so selection
  // doesn't clear during Berta's "thinking" pause. (#974)
  React.useEffect(() => {
    setSelectedTile(null);
  }, [displayState.chain.length, displayState.user_hand.length, displayState.current_player]);

  const handlePlace = useCallback((end) => {
    if (selectedTile === null) return;
    onPlay(selectedTile, end);
    // #974c — clear the selection IMMEDIATELY, don't wait for the
    // server's domino_state round-trip. Two reasons, both surfaced by
    // Bas's 2026-06-02 staging session (conv e6ce8616):
    //   1. INSTANT FEEDBACK. The green drop-target pulse vanishes the
    //      moment the user taps = a clear "got it". Pre-#974 Berta SPOKE
    //      a move-acknowledgment right after the user played, which
    //      masked the WebSocket round-trip latency; #974 removed that
    //      audio, so without an immediate visual cue the user can't tell
    //      their tap registered ("strange delay... I click often twice").
    //   2. NO DOUBLE-SEND. With the drop targets gone on the first tap, a
    //      second uncertain tap during the round-trip can't fire a second
    //      domino_play for a tile the server has already consumed — which
    //      was producing the "tile (x,y) not in user's hand" error toast
    //      (server log: "[domino] illegal user action: tile (3, 5) not in
    //      user's hand"). The selection-reset useEffect above still fires
    //      on the server's state push as a backstop (and on a rejected
    //      move, where no state change arrives, the immediate clear here
    //      is what lets the user cleanly re-select and retry).
    setSelectedTile(null);
  }, [selectedTile, onPlay]);

  const userIsCurrent = displayState.current_player === 'user';
  const userHasPlayableTile = useMemo(() => {
    if (displayState.chain.length === 0) return displayState.user_hand.length > 0;
    return displayState.user_hand.some((tile) =>
      tile[0] === displayState.left_end || tile[1] === displayState.left_end ||
      tile[0] === displayState.right_end || tile[1] === displayState.right_end
    );
  }, [displayState.user_hand, displayState.left_end, displayState.right_end, displayState.chain.length]);

  // Game area — fills the ActivityFrame content slot. The four board
  // pieces are built ONCE and arranged by layout below (#1096): a vertical
  // stack in portrait/tall, a 2-column board (chain + hand left, Berta +
  // knock rail right) in short landscape so the tiles never clip.
  const bertaEl = (
    <BertaStack count={displayState.berta_hand_count}
                isCurrentPlayer={displayState.current_player === 'berta'}
                companionName={companionName}
                thinking={bertaThinking}
                passed={bertaPassed} />
  );
  const chainEl = (
    <ChainArea
      chain={displayState.chain}
      leftEnd={displayState.left_end}
      rightEnd={displayState.right_end}
      selectedTile={selectedTile}
      onPlace={handlePlace}
      tileSize={tileSize}
    />
  );
  const handEl = (
    <UserHand
      tiles={displayState.user_hand}
      leftEnd={displayState.left_end}
      rightEnd={displayState.right_end}
      selectedTile={selectedTile}
      onSelect={setSelectedTile}
      isCurrentPlayer={userIsCurrent}
      tileSize={tileSize}
    />
  );
  const knockEl = (
    <button
      onClick={onKnock}
      disabled={!userIsCurrent || userHasPlayableTile}
      style={{
        background: (userIsCurrent && !userHasPlayableTile) ? BRAND_ORANGE : 'rgba(0,0,0,0.25)',
        color: '#F8F0DC',
        border: 'none',
        padding: '12px 32px',
        fontSize: '1.05rem', fontWeight: 600,
        borderRadius: 8,
        cursor: (userIsCurrent && !userHasPlayableTile) ? 'pointer' : 'not-allowed',
        minHeight: 60, minWidth: 140,
        opacity: (userIsCurrent && !userHasPlayableTile) ? 1 : 0.5,
      }}
      aria-label={tr.knockAria}
    >
      {tr.knock}
    </button>
  );

  const gameArea = (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      minWidth: 0,
      // #1089 — scroll as a last resort if the board still can't fit (e.g.
      // a box shorter than even the 48px tile floor can satisfy).
      overflowY: 'auto',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {shortLandscape ? (
        /* #1096 — 2-column board: chain + hand fill the wide left; Berta
           and the knock button sit in a compact right rail so a short
           landscape never clips the user's tiles at the bottom. */
        <div style={{
          flex: 1, display: 'flex', minHeight: 0, minWidth: 0,
          gap: 8, padding: '8px 12px',
        }}>
          <div style={{
            flex: '1 1 0', display: 'flex', flexDirection: 'column',
            minWidth: 0, minHeight: 0,
          }}>
            {chainEl}
            {handEl}
          </div>
          <div style={{
            flex: '0 0 auto', width: '24%', maxWidth: 240, minWidth: 120,
            display: 'flex', flexDirection: 'column',
            justifyContent: 'space-between', gap: 8, minHeight: 0,
          }}>
            {bertaEl}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {knockEl}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Top: Berta's stack */}
          <div style={{
            padding: '12px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            gap: 12,
          }}>
            <div style={{ flex: 1 }}>{bertaEl}</div>
          </div>

          {/* Middle: chain */}
          {chainEl}

          {/* Bottom: user's hand + knock button */}
          <div style={{ padding: '0 16px 16px' }}>
            {handEl}
            <div style={{
              marginTop: 12, display: 'flex', justifyContent: 'center',
            }}>
              {knockEl}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div style={{
      // #1089 — fills the ActivityFrame content slot (the frame owns the
      // full-screen positioning + z-index + close). Was fixed/inset with
      // a viewport-mode flex-direction branch for the embedded CallStrip.
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      position: 'relative',
      background: TABLE_GREEN,
      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)',
    }}>

      <style>{`
        @keyframes amg-domino-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.85; }
        }
      `}</style>

      {gameArea}

      {/* End-of-game overlay sits above everything else. Keyed off
          displayState so a game-ending Berta move still gets its
          client-side "thinking" beat before the end card appears. */}
      {displayState.winner && <GameEndCard winner={displayState.winner} onClose={onQuit} />}
    </div>
  );
}
